/**
 * Sentry noise filters for third-party / browser-extension failures that are
 * not actionable in our application code.
 *
 * ZENYT-PLATFORM-FRONTEND-PROD-A: a content script monkey-patched
 * `HTMLDivElement.appendChild` and called `JSON.stringify` on a React-managed
 * `HTMLAnchorElement` during login render. DOM nodes carry a circular
 * `__reactFiber$*` → fiber → `stateNode` cycle, so stringify throws. The
 * exception stack is only `react-dom` + `<anonymous>` frames (no first-party
 * app frames) — pure extension noise that otherwise opens a new Sentry issue
 * and trips the top-level ErrorBoundary.
 *
 * ZENYT-PLATFORM-FRONTEND-PROD-B: a Web3 wallet extension (e.g. Rabby) rejects
 * a promise with EIP-1193 code 4900 ("The provider is disconnected from all
 * chains.") from `chrome-extension://…/background.js`. Hub never talks to a
 * wallet provider; Sentry's unhandledrejection handler captures the plain
 * object as `Object captured as promise rejection with keys: code, message,
 * stack` with no first-party frames.
 *
 * ZENYT-PLATFORM-FRONTEND-PROD-C: React's commit phase calls `Node.removeChild`
 * (or `insertBefore`) after a browser extension / Chrome Translate / similar
 * tool has rewritten the live DOM. The node React expects to remove is no
 * longer a child of its parent, so the browser throws `NotFoundError`. This is
 * the signature Sentry themselves document as extension/translate noise
 * (ignoreErrors guidance); our direct `removeChild` helpers already try/catch,
 * so an uncaught copy of this message is not first-party app logic.
 */

import type { ErrorEvent } from "@sentry/react";

/**
 * Matches the V8 phrasing when stringify walks a React-owned DOM node:
 *   Converting circular structure to JSON
 *       --> starting at object with constructor 'HTMLAnchorElement'
 *       |     property '__reactFiber$…' -> object with constructor '…'
 *       --- property 'stateNode' closes the circle
 */
const CIRCULAR_DOM_JSON_RE =
  /Converting circular structure to JSON[\s\S]*constructor 'HTML[A-Za-z]+Element'[\s\S]*__reactFiber/i;

/** EIP-1193 / wallet-extension disconnect phrasing (MetaMask, Rabby, …). */
const WALLET_PROVIDER_DISCONNECT_RE =
  /provider is disconnected from all chains/i;

/**
 * React commit vs. external DOM mutation (extensions / Translate). Matches the
 * exact DOMException messages Sentry recommends ignoring for React apps.
 * Also accept the bare "Failed to execute …" value when `type` is already
 * `NotFoundError` (some browsers omit the type prefix in `exception.value`).
 */
const REACT_DOM_MUTATION_NOT_FOUND_RE =
  /(?:NotFoundError:\s*)?Failed to execute '(?:removeChild|insertBefore)' on 'Node': The node (?:to be removed|before which the new node is to be inserted) is not a child of this node/i;

/** Browser-extension URL scheme in a stack string or frame path. */
const EXTENSION_SCHEME_RE =
  /chrome-extension:|moz-extension:|safari-extension:/i;

type StackFrame = {
  filename?: string | null;
  abs_path?: string | null;
  module?: string | null;
  function?: string | null;
};

function errorEventMessages(event: ErrorEvent): string[] {
  const messages: string[] = [];
  for (const value of event.exception?.values ?? []) {
    if (value.type) messages.push(value.type);
    if (value.value) messages.push(value.value);
  }
  if (typeof event.message === "string") messages.push(event.message);
  return messages;
}

/**
 * Flatten Sentry `extra` (including `__serialized__` plain-object rejections)
 * into searchable strings. Wallet extensions reject with `{code, message,
 * stack}` which lands here rather than on `exception.values[].value`.
 */
function errorEventExtraText(event: ErrorEvent): string[] {
  const texts: string[] = [];
  const extra = event.extra;
  if (!extra || typeof extra !== "object") return texts;

  const visit = (value: unknown, depth: number): void => {
    if (depth > 4 || value == null) return;
    if (typeof value === "string" || typeof value === "number") {
      texts.push(String(value));
      return;
    }
    if (typeof value !== "object") return;
    if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1);
      return;
    }
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      texts.push(key);
      visit(child, depth + 1);
    }
  };

  visit(extra, 0);
  return texts;
}

function eventStackFrames(event: ErrorEvent): StackFrame[] {
  const frames: StackFrame[] = [];
  for (const value of event.exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      frames.push(frame);
    }
  }
  return frames;
}

/**
 * Whether a stack frame looks like first-party Hub / staging app code.
 * Production stacks use content-hashed `/assets/*.js` URLs; local / source-
 * mapped stacks use `/src/`. react-dom, node_modules, chrome-extension, and
 * blank/`<anonymous>` filenames are not ours.
 */
export function isAppStackFrame(frame: StackFrame): boolean {
  const path = `${frame.filename ?? ""} ${frame.abs_path ?? ""} ${frame.module ?? ""}`;
  if (!path.trim()) return false;
  if (EXTENSION_SCHEME_RE.test(path)) {
    return false;
  }
  if (/node_modules|react-dom/i.test(path)) return false;
  if (/\/assets\/[^/]+\.js/i.test(path)) return true;
  if (/\/src\//i.test(path)) return true;
  return false;
}

function hasAppStackFrames(event: ErrorEvent): boolean {
  return eventStackFrames(event).some(isAppStackFrame);
}

/**
 * True when the event is the known third-party circular-DOM-JSON signature
 * (ZENYT-PLATFORM-FRONTEND-PROD-A) and no first-party frame participated in
 * the throw — i.e. safe to drop as extension / injected-script noise.
 *
 * Deliberately does NOT drop a circular-JSON TypeError that originates in our
 * own `/assets/` or `/src/` frames (that would be a real app bug where we
 * stringify something holding a DOM node).
 */
export function isThirdPartyCircularDomJsonEvent(event: ErrorEvent): boolean {
  const messages = errorEventMessages(event);
  if (!messages.some((message) => CIRCULAR_DOM_JSON_RE.test(message))) {
    return false;
  }

  const frames = eventStackFrames(event);
  if (frames.length === 0) {
    // Message alone is specific enough (HTML*Element + __reactFiber path).
    return true;
  }

  return !frames.some(isAppStackFrame);
}

/**
 * True when the event is a Web3 wallet provider-disconnect rejection
 * (ZENYT-PLATFORM-FRONTEND-PROD-B) with no first-party app frames.
 *
 * Matches either:
 *   - the distinctive disconnect message anywhere on the event, or
 *   - EIP-1193 code 4900 plus an extension-scheme stack string in `extra`
 *     (Sentry's plain-object rejection serialization).
 */
export function isThirdPartyWalletProviderDisconnectEvent(
  event: ErrorEvent,
): boolean {
  if (hasAppStackFrames(event)) return false;

  const haystack = [
    ...errorEventMessages(event),
    ...errorEventExtraText(event),
  ].join("\n");

  if (WALLET_PROVIDER_DISCONNECT_RE.test(haystack)) {
    return true;
  }

  // code 4900 alone is too broad without extension attribution.
  const hasDisconnectCode = /\b4900\b/.test(haystack);
  const hasExtensionStack = EXTENSION_SCHEME_RE.test(haystack);
  return hasDisconnectCode && hasExtensionStack;
}

/**
 * True when the event is the known React `removeChild` / `insertBefore`
 * NotFoundError signature (ZENYT-PLATFORM-FRONTEND-PROD-C) caused by a
 * third party mutating the DOM under React.
 *
 * Matched on the DOMException message itself (Sentry's documented
 * `ignoreErrors` strings). Unlike PROD-A/B we do **not** require an empty
 * first-party stack: production builds often fold `react-dom` into
 * `/assets/*.js`, which would look "first-party" under `isAppStackFrame` and
 * incorrectly keep the noise.
 */
export function isThirdPartyReactDomMutationNotFoundEvent(
  event: ErrorEvent,
): boolean {
  return errorEventMessages(event).some((message) =>
    REACT_DOM_MUTATION_NOT_FOUND_RE.test(message),
  );
}

/**
 * Same signature as {@link isThirdPartyReactDomMutationNotFoundEvent}, for
 * live `Error` / `DOMException` instances (ErrorBoundary / route errors).
 */
export function isBenignReactDomMutationError(error: unknown): boolean {
  if (error == null) return false;
  const type =
    error instanceof Error
      ? error.name
      : typeof error === "object" &&
          error !== null &&
          "name" in error &&
          typeof (error as { name: unknown }).name === "string"
        ? (error as { name: string }).name
        : "";
  const value =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : typeof error === "object" &&
            error !== null &&
            "message" in error &&
            typeof (error as { message: unknown }).message === "string"
          ? (error as { message: string }).message
          : String(error);
  const haystack = `${type}: ${value}`;
  return REACT_DOM_MUTATION_NOT_FOUND_RE.test(haystack) ||
    REACT_DOM_MUTATION_NOT_FOUND_RE.test(value);
}

/**
 * `beforeSend` policy: drop known third-party / extension noise, otherwise
 * leave the event unchanged for downstream handlers (chunk-load, etc.).
 */
export function processThirdPartyNoiseEvent(
  event: ErrorEvent,
): ErrorEvent | null {
  if (isThirdPartyCircularDomJsonEvent(event)) {
    return null;
  }
  if (isThirdPartyWalletProviderDisconnectEvent(event)) {
    return null;
  }
  if (isThirdPartyReactDomMutationNotFoundEvent(event)) {
    return null;
  }
  return event;
}
